import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	createInitialState,
	stepSimulation,
	updatePlanetOrbit,
	computePredictedPath,
	setEscapeDistance,
	getEscapeDistance,
	vec2,
	length,
	normalize,
	scale,
	sub,
	dist,
	type SimState,
	type Vec2,
} from "./simulation";

const DEFAULT_ORBIT_RADIUS = 160;
const DEFAULT_PLANET_MASS = 300;
const DEFAULT_PLANET_SPEED = 80;
const DEFAULT_LAUNCH_SPEED = 180;

function generateStars(w: number, h: number) {
	const count = Math.floor((w * h) / 3000);
	const result: { x: number; y: number; r: number; brightness: number }[] = [];
	for (let i = 0; i < count; i++) {
		result.push({
			x: Math.random() * w,
			y: Math.random() * h,
			r: Math.random() * 1.5 + 0.3,
			brightness: Math.random() * 0.6 + 0.4,
		});
	}
	return result;
}

function drawStars(ctx: CanvasRenderingContext2D, time: number, stars: { x: number; y: number; r: number; brightness: number }[]) {
	for (const star of stars) {
		const twinkle = 0.7 + 0.3 * Math.sin(time * 2 + star.x * 0.1 + star.y * 0.1);
		const alpha = star.brightness * twinkle;
		ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
		ctx.beginPath();
		ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
		ctx.fill();
	}
}

function drawOrbitPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
	ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
	ctx.lineWidth = 1;
	ctx.setLineDash([4, 8]);
	ctx.beginPath();
	ctx.arc(cx, cy, radius, 0, Math.PI * 2);
	ctx.stroke();
	ctx.setLineDash([]);
}

function drawPlanet(ctx: CanvasRenderingContext2D, pos: Vec2, radius: number, time: number) {
	// Glow
	const gradient = ctx.createRadialGradient(pos.x, pos.y, radius * 0.5, pos.x, pos.y, radius * 3);
	gradient.addColorStop(0, "rgba(100, 160, 255, 0.3)");
	gradient.addColorStop(0.5, "rgba(60, 120, 220, 0.1)");
	gradient.addColorStop(1, "rgba(40, 80, 180, 0)");
	ctx.fillStyle = gradient;
	ctx.beginPath();
	ctx.arc(pos.x, pos.y, radius * 3, 0, Math.PI * 2);
	ctx.fill();

	// Planet body
	const bodyGrad = ctx.createRadialGradient(
		pos.x - radius * 0.3,
		pos.y - radius * 0.3,
		radius * 0.1,
		pos.x,
		pos.y,
		radius,
	);
	bodyGrad.addColorStop(0, "#8ec5fc");
	bodyGrad.addColorStop(0.6, "#4a7fd4");
	bodyGrad.addColorStop(1, "#1a3a6a");
	ctx.fillStyle = bodyGrad;
	ctx.beginPath();
	ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
	ctx.fill();

	// Atmospheric band
	ctx.strokeStyle = "rgba(150, 200, 255, 0.2)";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.ellipse(pos.x, pos.y, radius * 0.9, radius * 0.3, time * 0.1, 0, Math.PI * 2);
	ctx.stroke();
}

function drawCraft(ctx: CanvasRenderingContext2D, pos: Vec2, vel: Vec2, status: string, time: number) {
	if (status === "crashed") return;

	// Engine glow when flying
	if (status === "flying") {
		const glowGrad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, 12);
		glowGrad.addColorStop(0, "rgba(255, 200, 50, 0.4)");
		glowGrad.addColorStop(1, "rgba(255, 100, 20, 0)");
		ctx.fillStyle = glowGrad;
		ctx.beginPath();
		ctx.arc(pos.x, pos.y, 12, 0, Math.PI * 2);
		ctx.fill();
	}

	// Craft body
	const angle = status === "flying" || status === "escaped" ? Math.atan2(vel.y, vel.x) : 0;
	ctx.save();
	ctx.translate(pos.x, pos.y);
	ctx.rotate(angle);

	// Triangle ship
	ctx.fillStyle = status === "aiming" ? "#ffffff" : "#ffdd44";
	ctx.beginPath();
	ctx.moveTo(8, 0);
	ctx.lineTo(-5, -4);
	ctx.lineTo(-3, 0);
	ctx.lineTo(-5, 4);
	ctx.closePath();
	ctx.fill();

	// Pulsing highlight
	const pulse = 0.5 + 0.5 * Math.sin(time * 6);
	ctx.fillStyle = `rgba(255, 255, 200, ${pulse * 0.5})`;
	ctx.beginPath();
	ctx.arc(0, 0, 2, 0, Math.PI * 2);
	ctx.fill();

	ctx.restore();
}

function drawTrail(ctx: CanvasRenderingContext2D, trail: Vec2[]) {
	if (trail.length < 2) return;

	for (let i = 1; i < trail.length; i++) {
		const t = i / trail.length;
		const alpha = t * 0.8;
		const r = Math.round(255 * (1 - t) + 255 * t);
		const g = Math.round(150 * (1 - t) + 200 * t);
		const b = Math.round(50 * (1 - t) + 100 * t);
		ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
		ctx.lineWidth = 1 + t * 1.5;
		ctx.beginPath();
		ctx.moveTo(trail[i - 1].x, trail[i - 1].y);
		ctx.lineTo(trail[i].x, trail[i].y);
		ctx.stroke();
	}
}

function drawPredictedPath(ctx: CanvasRenderingContext2D, path: Vec2[]) {
	if (path.length < 2) return;

	ctx.setLineDash([3, 6]);
	for (let i = 1; i < path.length; i++) {
		const t = i / path.length;
		ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * (1 - t)})`;
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(path[i - 1].x, path[i - 1].y);
		ctx.lineTo(path[i].x, path[i].y);
		ctx.stroke();
	}
	ctx.setLineDash([]);
}

function drawAimArrow(ctx: CanvasRenderingContext2D, pos: Vec2, angle: number, speed: number) {
	const len = 30 + (speed / 300) * 50;
	const dir = vec2(Math.cos(angle), Math.sin(angle));
	const tip = { x: pos.x + dir.x * len, y: pos.y + dir.y * len };

	ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(pos.x, pos.y);
	ctx.lineTo(tip.x, tip.y);
	ctx.stroke();

	// Arrowhead
	const headLen = 8;
	const headAngle = 0.4;
	ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
	ctx.beginPath();
	ctx.moveTo(tip.x, tip.y);
	ctx.lineTo(
		tip.x - headLen * Math.cos(angle - headAngle),
		tip.y - headLen * Math.sin(angle - headAngle),
	);
	ctx.lineTo(
		tip.x - headLen * Math.cos(angle + headAngle),
		tip.y - headLen * Math.sin(angle + headAngle),
	);
	ctx.closePath();
	ctx.fill();
}

function drawEscapeBoundary(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
	ctx.strokeStyle = "rgba(255, 100, 100, 0.1)";
	ctx.lineWidth = 1;
	ctx.setLineDash([8, 12]);
	ctx.beginPath();
	ctx.arc(cx, cy, radius, 0, Math.PI * 2);
	ctx.stroke();
	ctx.setLineDash([]);
}

function drawHUD(ctx: CanvasRenderingContext2D, state: SimState, canvasW: number) {
	ctx.font = "12px monospace";
	ctx.fillStyle = "rgba(255, 255, 255, 0.6)";

	const speed = length(state.craft.vel);
	ctx.fillText(`Speed: ${speed.toFixed(1)}`, 16, 28);
	ctx.fillText(`Time: ${state.time.toFixed(1)}s`, 16, 46);

	if (state.status === "escaped") {
		const gain = state.speedGain;
		const pct = state.initialSpeed > 0 ? ((gain / state.initialSpeed) * 100).toFixed(0) : "N/A";
		ctx.font = "bold 16px monospace";
		ctx.fillStyle = gain > 0 ? "#44ff88" : "#ff6644";
		ctx.fillText(
			gain > 0 ? `SLINGSHOT SUCCESS! +${pct}% speed` : `Speed lost: ${pct}%`,
			canvasW / 2 - 120,
			40,
		);
		ctx.font = "13px monospace";
		ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
		ctx.fillText(
			`Entry: ${state.initialSpeed.toFixed(1)}  Exit: ${state.exitSpeed.toFixed(1)}  Gain: ${gain > 0 ? "+" : ""}${gain.toFixed(1)}`,
			canvasW / 2 - 160,
			64,
		);
	} else if (state.status === "crashed") {
		ctx.font = "bold 16px monospace";
		ctx.fillStyle = "#ff4444";
		ctx.fillText("CRASHED!", canvasW / 2 - 40, 40);
		ctx.font = "12px monospace";
		ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
		ctx.fillText("Press Reset to try again", canvasW / 2 - 75, 60);
	}
}

function drawCrashEffect(ctx: CanvasRenderingContext2D, pos: Vec2, time: number) {
	const t = Math.min(time, 1);
	for (let i = 0; i < 8; i++) {
		const angle = (i / 8) * Math.PI * 2 + time * 2;
		const dist = t * 40;
		const px = pos.x + Math.cos(angle) * dist;
		const py = pos.y + Math.sin(angle) * dist;
		const alpha = 1 - t;
		ctx.fillStyle = `rgba(255, ${100 + i * 20}, 50, ${alpha})`;
		ctx.beginPath();
		ctx.arc(px, py, 3 * (1 - t * 0.5), 0, Math.PI * 2);
		ctx.fill();
	}
}

export function GravitySlingshot() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const stateRef = useRef<SimState | null>(null);
	const animRef = useRef<number>(0);
	const crashTimeRef = useRef<number>(0);
	const lastFrameRef = useRef<number>(0);
	const aimAngleRef = useRef<number>(-0.5);
	const mouseRef = useRef<Vec2 | null>(null);
	const starsRef = useRef<{ x: number; y: number; r: number; brightness: number }[]>([]);
	const sizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

	const [status, setStatus] = useState<SimState["status"]>("aiming");
	const [launchSpeed, setLaunchSpeed] = useState(DEFAULT_LAUNCH_SPEED);
	const [planetMass, setPlanetMass] = useState(DEFAULT_PLANET_MASS);
	const [planetSpeed, setPlanetSpeed] = useState(DEFAULT_PLANET_SPEED);
	const [orbitRadius, setOrbitRadius] = useState(DEFAULT_ORBIT_RADIUS);
	const [resultInfo, setResultInfo] = useState<string>("");
	const [controlsOpen, setControlsOpen] = useState(true);

	const resizeCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const w = window.innerWidth;
		const h = window.innerHeight;
		canvas.width = w;
		canvas.height = h;
		sizeRef.current = { w, h };
		starsRef.current = generateStars(w, h);
		// Scale escape distance to ~80% of the half-diagonal so it works on any screen size
		const halfDiag = Math.sqrt(w * w + h * h) / 2;
		setEscapeDistance(Math.max(halfDiag * 0.8, 600));
	}, []);

	const resetSim = useCallback(() => {
		const { w, h } = sizeRef.current;
		if (w === 0 || h === 0) return;
		const state = createInitialState(w, h, orbitRadius, planetMass, planetSpeed);
		stateRef.current = state;
		crashTimeRef.current = 0;
		lastFrameRef.current = 0;
		setStatus("aiming");
		setResultInfo("");
	}, [orbitRadius, planetMass, planetSpeed]);

	const launch = useCallback(() => {
		const state = stateRef.current;
		if (!state || state.status !== "aiming") return;

		const angle = aimAngleRef.current;
		state.craft.vel = {
			x: Math.cos(angle) * launchSpeed,
			y: Math.sin(angle) * launchSpeed,
		};
		state.initialSpeed = launchSpeed;
		state.status = "flying";
		setStatus("flying");
	}, [launchSpeed]);

	// Initialize canvas size and handle resize
	useEffect(() => {
		resizeCanvas();
		const onResize = () => {
			resizeCanvas();
		};
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, [resizeCanvas]);

	// Initialize simulation after canvas is sized
	useEffect(() => {
		resetSim();
	}, [resetSim]);

	// Handle mouse for aiming
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const handleMouseMove = (e: MouseEvent) => {
			const mx = e.clientX;
			const my = e.clientY;
			mouseRef.current = vec2(mx, my);

			const state = stateRef.current;
			if (state && state.status === "aiming") {
				const dx = mx - state.craft.pos.x;
				const dy = my - state.craft.pos.y;
				aimAngleRef.current = Math.atan2(dy, dx);
			}
		};

		const handleClick = (e: MouseEvent) => {
			// Ignore clicks on the controls panel
			const target = e.target as HTMLElement;
			if (target.closest("[data-controls]")) return;

			const state = stateRef.current;
			if (state && state.status === "aiming") {
				launch();
			}
		};

		canvas.addEventListener("mousemove", handleMouseMove);
		canvas.addEventListener("click", handleClick);

		return () => {
			canvas.removeEventListener("mousemove", handleMouseMove);
			canvas.removeEventListener("click", handleClick);
		};
	}, [launch]);

	// Animation loop
	useEffect(() => {
		const animate = (timestamp: number) => {
			const canvas = canvasRef.current;
			const state = stateRef.current;
			if (!canvas || !state) {
				animRef.current = requestAnimationFrame(animate);
				return;
			}

			const CANVAS_W = canvas.width;
			const CANVAS_H = canvas.height;
			const cx = CANVAS_W / 2;
			const cy = CANVAS_H / 2;

			const ctx = canvas.getContext("2d")!;
			const dt = lastFrameRef.current === 0 ? 0.016 : Math.min((timestamp - lastFrameRef.current) / 1000, 0.05);
			lastFrameRef.current = timestamp;

			// Update planet position in aiming mode
			if (state.status === "aiming") {
				state.time += dt;
				updatePlanetOrbit(state.planet, cx, cy, orbitRadius, planetSpeed, state.time);
			}

			// Step physics
			if (state.status === "flying") {
				stepSimulation(state, dt, cx, cy, orbitRadius, planetSpeed);
				if (state.status !== "flying") {
					setStatus(state.status);
					if (state.status === "escaped") {
						const gain = state.speedGain;
						const pct = state.initialSpeed > 0 ? ((gain / state.initialSpeed) * 100).toFixed(0) : "N/A";
						setResultInfo(
							gain > 0
								? `Slingshot: +${pct}% speed gain (${state.initialSpeed.toFixed(0)} -> ${state.exitSpeed.toFixed(0)})`
								: `Speed lost: ${pct}% (${state.initialSpeed.toFixed(0)} -> ${state.exitSpeed.toFixed(0)})`,
						);
					} else if (state.status === "crashed") {
						crashTimeRef.current = 0;
					}
				}
			}

			if (state.status === "crashed") {
				crashTimeRef.current += dt;
			}

			// Compute predicted path while aiming
			if (state.status === "aiming") {
				const angle = aimAngleRef.current;
				const vel = vec2(Math.cos(angle) * launchSpeed, Math.sin(angle) * launchSpeed);
				state.predictedPath = computePredictedPath(
					state.craft.pos,
					vel,
					state.planet,
					cx,
					cy,
					orbitRadius,
					planetSpeed,
					state.time,
				);
			}

			// --- RENDER ---
			ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

			// Background
			const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, CANVAS_W * 0.7);
			bgGrad.addColorStop(0, "#0a0a1a");
			bgGrad.addColorStop(0.5, "#060612");
			bgGrad.addColorStop(1, "#020208");
			ctx.fillStyle = bgGrad;
			ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

			drawStars(ctx, state.time, starsRef.current);
			drawEscapeBoundary(ctx, cx, cy, getEscapeDistance());
			drawOrbitPath(ctx, cx, cy, orbitRadius);

			// Center marker
			ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
			ctx.beginPath();
			ctx.arc(cx, cy, 3, 0, Math.PI * 2);
			ctx.fill();

			drawPlanet(ctx, state.planet.pos, state.planet.radius, state.time);
			drawTrail(ctx, state.trail);

			if (state.status === "aiming") {
				drawPredictedPath(ctx, state.predictedPath);
				drawAimArrow(ctx, state.craft.pos, aimAngleRef.current, launchSpeed);
			}

			drawCraft(ctx, state.craft.pos, state.craft.vel, state.status, state.time);

			if (state.status === "crashed") {
				drawCrashEffect(ctx, state.craft.pos, crashTimeRef.current);
			}

			drawHUD(ctx, state, CANVAS_W);

			animRef.current = requestAnimationFrame(animate);
		};

		animRef.current = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(animRef.current);
	}, [orbitRadius, planetSpeed, launchSpeed, planetMass]);

	return (
		<div className="fixed inset-0 overflow-hidden">
			<canvas
				ref={canvasRef}
				className="block w-full h-full cursor-crosshair"
			/>

			{/* Title overlay */}
			<div className="absolute top-4 left-1/2 -translate-x-1/2 text-center pointer-events-none">
				<h1 className="text-xl font-bold text-white/80 tracking-tight drop-shadow-lg">
					Gravitational Slingshot
				</h1>
				<p className="text-xs text-white/40 mt-0.5">
					Aim and click to launch. Use gravity to gain speed.
				</p>
			</div>

			{/* Result banner */}
			{resultInfo && (
				<div className="absolute top-14 left-1/2 -translate-x-1/2 pointer-events-none">
					<div
						className={`text-sm font-mono px-4 py-2 rounded-lg backdrop-blur-sm ${
							status === "escaped" && stateRef.current && stateRef.current.speedGain > 0
								? "bg-green-500/10 text-green-400 border border-green-500/20"
								: "bg-red-500/10 text-red-400 border border-red-500/20"
						}`}
					>
						{resultInfo}
					</div>
				</div>
			)}

			{/* Floating controls */}
			<div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-full max-w-[700px] px-4" data-controls>
				<Card className="bg-card/70 backdrop-blur-md border-border/50 shadow-2xl">
					<CardContent className="pt-3 pb-3">
						<button
							onClick={() => setControlsOpen(!controlsOpen)}
							className="w-full text-xs text-muted-foreground hover:text-foreground mb-1 transition-colors"
						>
							{controlsOpen ? "Hide Controls" : "Show Controls"}
						</button>
						{controlsOpen && (
							<>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
									<div className="flex flex-col gap-1.5">
										<label className="text-xs font-medium text-muted-foreground">
											Launch Speed: {launchSpeed}
										</label>
										<input
											type="range"
											min="60"
											max="350"
											value={launchSpeed}
											onChange={(e) => setLaunchSpeed(Number(e.target.value))}
											disabled={status === "flying"}
											className="accent-primary h-2"
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className="text-xs font-medium text-muted-foreground">
											Planet Mass: {planetMass}
										</label>
										<input
											type="range"
											min="100"
											max="800"
											value={planetMass}
											onChange={(e) => setPlanetMass(Number(e.target.value))}
											disabled={status === "flying"}
											className="accent-primary h-2"
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className="text-xs font-medium text-muted-foreground">
											Planet Speed: {planetSpeed}
										</label>
										<input
											type="range"
											min="20"
											max="200"
											value={planetSpeed}
											onChange={(e) => setPlanetSpeed(Number(e.target.value))}
											disabled={status === "flying"}
											className="accent-primary h-2"
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<label className="text-xs font-medium text-muted-foreground">
											Orbit Radius: {orbitRadius}
										</label>
										<input
											type="range"
											min="80"
											max="300"
											value={orbitRadius}
											onChange={(e) => setOrbitRadius(Number(e.target.value))}
											disabled={status === "flying"}
											className="accent-primary h-2"
										/>
									</div>
								</div>

								<div className="flex gap-3 mt-3 justify-center">
									<Button
										onClick={(e) => { e.stopPropagation(); launch(); }}
										disabled={status !== "aiming"}
										className="px-6"
										size="sm"
									>
										Launch
									</Button>
									<Button onClick={(e) => { e.stopPropagation(); resetSim(); }} variant="outline" className="px-6" size="sm">
										Reset
									</Button>
								</div>
							</>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
